'use strict';

const { PACS_INTEGRACAO } = require('../../constantes');

/**
 * Factory para carregar o modelo de Notificação do fluxo de Invalidação PACS.
 *
 * @param {Object} deps
 * @param {import('mongoose').Connection} deps.connection - Conexão mongoose onde os models serão registrados.
 * @param {Function} [deps.reconnect] - Função opcional de reconexão. Se fornecida, é chamada quando readyState !== 1.
 * @param {Object} [deps.logger] - Logger opcional ({ info, error }). Default: console.
 */
const carregarModelo = ({ connection, reconnect, logger } = {}) => {
    if (!connection) {
        throw new Error('notificacaoInvalidacao: parâmetro `connection` é obrigatório');
    }

    const log = logger || console;

    // cópias diferentes de mongoose em node_modules distintos.
    const mongoose = connection.base;

    const notificacaoInvalidacaoMensagensSchema = new mongoose.Schema(
        {
            study_iuid: { type: String, required: true, index: true },
            pacs_aetitle: { type: String, required: true, index: true },
            tipo_pacs: { type: String },
            bridge_aetitle: { type: String },
            comando_id: { type: String },
            id_exame: { type: String },
            status: { type: Number },
            msg: { type: String },
            stacktrace: { type: String },
            projeto: { type: String },
            last_update: { type: Date, default: Date.now, required: true }
        },
        { collection: 'notificacao_invalidacao_mensagens' }
    );

    notificacaoInvalidacaoMensagensSchema.index({ study_iuid: 1, pacs_aetitle: 1, last_update: -1 });
    notificacaoInvalidacaoMensagensSchema.index({ comando_id: 1 });

    const notificacaoInvalidacaoSchema = new mongoose.Schema(
        {
            study_iuid: { type: String, required: true, minlength: 1 },
            pacs_aetitle: { type: String, required: true, minlength: 1 },
            pacs_url: { type: String },
            created_date: { type: Date, default: Date.now, required: true },
            last_update: { type: Date, default: Date.now, required: true },
            ultimo_status: { type: Number },
            status: { type: Number },
            msg: { type: String },
            pacs_origem_aetitle: { type: String }
        },
        {
            collection: 'notificacao_invalidacao',
            indexes: [{ fields: { study_iuid: 1, pacs_aetitle: 1 }, unique: true }]
        }
    );

    // Reaproveita model já registrado na mesma conexão — evita OverwriteModelError
    // quando a factory é chamada mais de uma vez com a mesma connection.
    const NotificacaoInvalidacao = connection.models.NotificacaoInvalidacao || connection.model('NotificacaoInvalidacao', notificacaoInvalidacaoSchema);
    const NotificacaoInvalidacaoMensagens = connection.models.NotificacaoInvalidacaoMensagens || connection.model('NotificacaoInvalidacaoMensagens', notificacaoInvalidacaoMensagensSchema);

    async function garantirConexao() {
        if (connection.readyState !== 1) {
            log.info && log.info('Tentando reconectar ao MongoDB...');
            if (typeof reconnect === 'function') {
                await reconnect();
            }
        }
    }

    async function recuperarInvalidacao({ study_iuid, pacs_url, pacs_aetitle }) {
        await garantirConexao();
        try {
            const documento = await NotificacaoInvalidacao.findOne({ study_iuid, pacs_url, pacs_aetitle });
            if (!documento) return null;

            const mensagens = await NotificacaoInvalidacaoMensagens.find({
                study_iuid,
                pacs_aetitle
            }).sort({ last_update: -1 });

            return {
                study_iuid: documento.study_iuid,
                pacs_url: documento.pacs_url,
                pacs_aetitle: documento.pacs_aetitle,
                history: mensagens.map((msg) => ({
                    status: msg.status,
                    msg: msg.msg,
                    last_update: msg.last_update
                }))
            };
        } catch (e) {
            if (e instanceof mongoose.Error.ValidationError) {
                log.error ? log.error(`Erro na validação: ${e.message}`) : console.log(`Erro na validação: ${e.message}`);
            } else {
                log.error ? log.error(`Erro ao recuperar o documento: ${e.message}`) : console.log(`Erro ao recuperar o documento: ${e.message}`);
            }
            throw e;
        }
    }

    async function atualizarNotificacaoInvalidacao({ study_iuid, pacs_url, pacs_aetitle, status, msg, pacs_origem_aetitle }) {
        await garantirConexao();

        const last_update = new Date();
        const created_date = new Date();

        status = status ?? null;
        msg = msg ?? null;
        pacs_origem_aetitle = pacs_origem_aetitle ?? null;

        try {
            let documentoInvalidacao = await NotificacaoInvalidacao.findOne({ study_iuid, pacs_url, pacs_aetitle });

            if (documentoInvalidacao) {
                documentoInvalidacao.last_update = last_update;
                documentoInvalidacao.ultimo_status = documentoInvalidacao.status;
                if (pacs_origem_aetitle !== null) documentoInvalidacao.pacs_origem_aetitle = pacs_origem_aetitle;
                if (documentoInvalidacao.status === PACS_INTEGRACAO.STATUS_INVALIDADO) {
                    log.info && log.info('Estudo já está com status INVALIDADO, não será alterado.');
                } else {
                    documentoInvalidacao.status = status;
                    documentoInvalidacao.msg = msg;
                }
            } else {
                documentoInvalidacao = new NotificacaoInvalidacao({
                    study_iuid,
                    pacs_url,
                    pacs_aetitle,
                    status,
                    ultimo_status: status,
                    last_update,
                    created_date,
                    msg,
                    pacs_origem_aetitle
                });
            }
            await documentoInvalidacao.save();

            const novoDocumentoMensagem = new NotificacaoInvalidacaoMensagens({
                study_iuid,
                pacs_aetitle,
                status,
                msg,
                last_update
            });
            await novoDocumentoMensagem.save();

            log.info && log.info('Documento atualizado com sucesso!');
            log.info && log.info('Mensagem de invalidacao inserida com sucesso!');
        } catch (e) {
            if (e instanceof mongoose.Error.ValidationError) {
                log.error ? log.error(`Erro na validação: ${e.message}`) : console.log(`Erro na validação: ${e.message}`);
            } else {
                log.error ? log.error(`Erro ao inserir ou atualizar o documento: ${e.message}`) : console.log(`Erro ao inserir ou atualizar o documento: ${e.message}`);
            }
            throw e;
        }
    }

    async function armazenarNotificacaoInvalidacao({ study_iuid, pacs_aetitle, tipo_pacs, bridge_aetitle, comando_id, id_exame, status, msg, stacktrace, projeto }) {
        await garantirConexao();

        const last_update = new Date();
        const created_date = new Date();

        // Protobuf trafega strings não preenchidas como "" — normalizar para null antes de persistir.
        const emptyToNull = (v) => (v === '' || v === undefined ? null : v);

        status = status ?? null;
        msg = emptyToNull(msg);
        tipo_pacs = emptyToNull(tipo_pacs);
        bridge_aetitle = emptyToNull(bridge_aetitle);
        comando_id = emptyToNull(comando_id);
        id_exame = emptyToNull(id_exame);
        stacktrace = emptyToNull(stacktrace);
        projeto = emptyToNull(projeto);

        try {
            let documentoInvalidacao = await NotificacaoInvalidacao.findOne({ study_iuid, pacs_aetitle });

            if (documentoInvalidacao) {
                documentoInvalidacao.last_update = last_update;
                documentoInvalidacao.ultimo_status = documentoInvalidacao.status;
                if (documentoInvalidacao.status === PACS_INTEGRACAO.STATUS_INVALIDADO) {
                    log.info && log.info('Estudo já está com status INVALIDADO, não será alterado.');
                } else {
                    documentoInvalidacao.status = status;
                    documentoInvalidacao.msg = msg;
                }
            } else {
                documentoInvalidacao = new NotificacaoInvalidacao({
                    study_iuid,
                    pacs_aetitle,
                    status,
                    ultimo_status: status,
                    last_update,
                    created_date,
                    msg
                });
            }
            await documentoInvalidacao.save();

            const novoDocumentoMensagem = new NotificacaoInvalidacaoMensagens({
                study_iuid,
                pacs_aetitle,
                tipo_pacs,
                bridge_aetitle,
                comando_id,
                id_exame,
                status,
                msg,
                stacktrace,
                projeto,
                last_update
            });
            await novoDocumentoMensagem.save();

            log.info && log.info(`Notificação de invalidação armazenada: study_iuid=${study_iuid} pacs_aetitle=${pacs_aetitle} comando_id=${comando_id}.`);
        } catch (e) {
            if (e instanceof mongoose.Error.ValidationError) {
                log.error ? log.error(`Erro na validação: ${e.message}`) : console.log(`Erro na validação: ${e.message}`);
            } else {
                log.error ? log.error(`Erro ao armazenar notificação de invalidação: ${e.message}`) : console.log(`Erro ao armazenar notificação de invalidação: ${e.message}`);
            }
            throw e;
        }
    }

    return {
        NotificacaoInvalidacao,
        NotificacaoInvalidacaoMensagens,
        recuperarInvalidacao,
        atualizarNotificacaoInvalidacao,
        armazenarNotificacaoInvalidacao
    };
};

module.exports = { carregarModelo };
